import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, CheckCircle2, Download, FileSignature, FileText, Plus, Search } from 'lucide-react';
import { apiClient } from '../../api/client';
import type { ApiResponse } from '../../types/api-manifest';
import { calendarDayString, formatCalendarDate, todayCalendarDate, tenantTodayDate, toCalendarDateString } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { buildBolCsv } from './bol-csv';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { unwrapArray } from '../../lib/api-envelope';
import { loadStatusSkin } from '../../lib/load-status';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
// The shared load_status badge. The local map this replaced fell back to
// `draft`'s look for any status it did not know.
import LoadStatusBadge from '../../components/trucking/LoadStatusBadge';
import { BilledOnLink, MultipleInvoicesBadge, NoInvoiceBadge } from '../../components/trucking/TruckingInvoiceCard';
import { useAuth } from '../../contexts/AuthContext';
import { bolCoverage } from '../../lib/trucking-invoice-status';

/**
 * Bills of Lading. There is no BOL table: every `loads` row is a BOL, so the
 * create action is New Load, not "New BOL".
 *
 * GET /trucking/loads/bols caps its list (100 unless asked, at most 500). This
 * page asks for 500. A list that comes back AT the cap may be missing older
 * BOLs, so every tally on the page becomes a dash and the footer says so; a
 * count of a truncated list is not a count of BOLs.
 *
 * GONE: "Total Value", which summed `flat_haul_rate` with a missing rate as $0
 * and ignored billing_type, so an hourly or per-km load added nothing. That is
 * the same COALESCE flaw as the rentals money on #815; the band counts BOLs
 * instead. "This Week" matched every BOL scheduled from a week ago into the
 * future, so it is "Last 7 days" now: the tenant's today and the six days
 * before it. "Unsigned" no longer counts cancelled loads.
 *
 * "OPEN", NOT "UNSIGNED" (fix/trucking-figures-agree). This page counted every
 * load not yet signed, booked ones included, under "Unsigned"; Dispatch and
 * the Overview used the same word for the API's `bol_pending` (delivered, not
 * yet signed). Three pages, one word, 7 vs 0 vs 0 on the same morning. The
 * chip and stat here are "Open"; "To sign" is the API's figure, on the pages
 * that show it. Both are no longer than the copy they replace (the band's
 * third column has a ~70px label ceiling).
 *
 * Invoice coverage (#815, api#1011) is each BOL's own `billed_on` /
 * `billed_on_count`, read by the API from the invoice LINES: "No invoice" on a
 * signed BOL whose `billed_on` is null (how the office sees a signature whose
 * auto-draft the API refused), the invoice number as a link when it is set,
 * and "Billed on N invoices" when the API counts more than one. A row whose
 * read carries no coverage key says nothing. Shown to the invoice read roles
 * only; a driver could not open the invoice.
 *
 * The rows hold a link, so they follow Rate Sheets (CLAUDE.md, fe#647): no
 * button role, Enter opens only from the row itself, and the link stops the
 * row's click. A mobile card is a button, so its invoice link sits under it.
 */
const BOL_LIMIT = 500;

type ChipKey = 'all' | 'today' | 'week' | 'unsigned' | 'signed';
const CHIPS: { key: ChipKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Last 7 days' },
  { key: 'unsigned', label: 'Open' },
  { key: 'signed', label: 'Signed' },
];

/** A calendar day as a month name ("Oct 2, 2026"); a bare numeric day is ambiguous across locales. */
const fmtDay = (d: unknown) => formatCalendarDate(typeof d === 'string' ? d : null) ?? '—';

export default function BillsOfLading() {
  const navigate = useNavigate();
  // Today, the last seven days, and the CSV's "signed on" day are the TENANT's
  // (companies.time_zone), not the dispatcher's device's.
  const timeZone = useTenantTimeZone();
  const [chip, setChip] = useState<ChipKey>('all');
  const [search, setSearch] = useState('');
  const [truckFilter, setTruckFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');

  const bolsQuery = useQuery({
    queryKey: ['trucking-bols'],
    queryFn: () => apiClient.get<ApiResponse<'GET /api/v1/trucking/loads/bols'>>('/trucking/loads/bols', { params: { limit: BOL_LIMIT } }).then(r => unwrapArray<any>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(bolsQuery) != null;
  const loading = bolsQuery.isLoading && !failed;
  const bols: any[] | undefined = failed ? undefined : bolsQuery.data;
  const capped = bols != null && bols.length >= BOL_LIMIT;

  const { user } = useAuth();
  const canReadInvoices = ['admin', 'office_staff', 'supervisor', 'dispatcher'].includes((user as { role?: string } | null)?.role ?? '');
  const coverageOf = (bol: any) => (canReadInvoices ? bolCoverage(bol) : ({ kind: 'unknown' } as const));
  const noInvoice = (bol: any) => bol.status === 'signed' && coverageOf(bol).kind === 'none';

  // scheduled_date is a calendar day, so both sides of these comparisons are
  // calendar days, and "today" is the TENANT's today. With no zone neither
  // chip matches anything rather than guessing a day.
  const todayStr = todayCalendarDate(timeZone);
  const weekStart = useMemo(() => {
    const d = tenantTodayDate(timeZone);
    if (!d) return null;
    d.setDate(d.getDate() - 6);
    return toCalendarDateString(d);
  }, [timeZone]);

  const matches = useMemo(() => {
    const day = (b: any) => calendarDayString(b.scheduled_date);
    return {
      all: () => true,
      today: (b: any) => todayStr !== null && day(b) === todayStr,
      week: (b: any) => {
        const d = day(b);
        return d != null && todayStr !== null && weekStart !== null && d >= weekStart && d <= todayStr;
      },
      // Not yet signed, and not cancelled: a cancelled load awaits no signature.
      unsigned: (b: any) => b.status !== 'signed' && b.status !== 'cancelled',
      signed: (b: any) => b.status === 'signed',
    } as Record<ChipKey, (b: any) => boolean>;
  }, [todayStr, weekStart]);

  const filtered = useMemo(() => {
    let items = (bols ?? []).filter(matches[chip]);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(b =>
        b.bol_number?.toLowerCase().includes(q) ||
        b.driver_name?.toLowerCase().includes(q) ||
        b.customer_name?.toLowerCase().includes(q)
      );
    }
    if (truckFilter) items = items.filter(b => b.truck_unit_number === truckFilter);
    if (customerFilter) items = items.filter(b => b.customer_name === customerFilter);
    return items;
  }, [bols, matches, chip, search, truckFilter, customerFilter]);

  const distinctTrucks = useMemo(() =>
    Array.from(new Set((bols ?? []).map((b: any) => b.truck_unit_number).filter(Boolean))).sort() as string[],
    [bols]
  );
  const distinctCustomers = useMemo(() =>
    Array.from(new Set((bols ?? []).map((b: any) => b.customer_name).filter(Boolean))).sort() as string[],
    [bols]
  );

  /**
   * Tallies of the loaded list. Honest only while the list is whole: at the
   * cap, or before it loads, there is no count to give.
   */
  const countOf = (k: ChipKey): number | null => (bols && !capped ? bols.filter(matches[k]).length : null);
  const fig = (k: ChipKey) => displayValue(countOf(k));

  function exportCsv() {
    // The CSV's "signed on" column is a day in the tenant's zone; with no zone
    // there is no honest day to write, so no file is written.
    if (!timeZone) return;
    const csv = buildBolCsv(filtered, timeZone);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bills-of-lading-${todayCalendarDate(timeZone)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const open = (id: string) => navigate(`/trucking/bols/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };
  const clearFilters = () => { setSearch(''); setTruckFilter(''); setCustomerFilter(''); setChip('all'); };
  const selectCls = 'rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none';

  return (
    <div data-testid="bills-of-lading-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking"
        title="Bills of Lading"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            <button
              type="button"
              onClick={exportCsv}
              disabled={filtered.length === 0 || !timeZone}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="size-4" aria-hidden="true" />
              Export CSV
            </button>
            <button type="button" onClick={() => navigate('/trucking/new-load')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New Load
            </button>
          </>
        }
        stats={[
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'BOLs', sub: 'One per load', tone: 'neutral', loading, value: fig('all') },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Today', sub: 'Scheduled today', tone: 'info', loading, value: fig('today') },
          { icon: <FileSignature className="size-4" aria-hidden="true" />, label: 'Open', sub: 'Not signed', tone: 'accent', loading, value: fig('unsigned') },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Signed', sub: 'Customer signed', tone: 'success', loading, value: fig('signed') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {CHIPS.map((c) => {
              const active = chip === c.key;
              const n = countOf(c.key);
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setChip(c.key)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {c.label}
                  {!loading && n != null && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{n}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search BOL number, driver, customer..."
                aria-label="Search bills of lading"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-fieldiq-muted">
              Truck
              <select value={truckFilter} onChange={e => setTruckFilter(e.target.value)} className={selectCls}>
                <option value="">All trucks</option>
                {distinctTrucks.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs font-semibold text-fieldiq-muted">
              Customer
              <select value={customerFilter} onChange={e => setCustomerFilter(e.target.value)} className={selectCls}>
                <option value="">All customers</option>
                {distinctCustomers.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          <section
            aria-labelledby="bol-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">BOL register</p>
              <h2 id="bol-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Bills of lading
              </h2>
            </header>

            {failed ? (
              <LoadFailureNotice error={queryFailureError(bolsQuery)} subject="bills of lading" />
            ) : loading ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-12 text-center">
                {(bols ?? []).length === 0 ? (
                  <>
                    <p className="text-sm font-semibold text-fieldiq-text">No bills of lading yet</p>
                    <p className="mt-1 text-sm text-fieldiq-muted">Every load you book is a BOL.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-fieldiq-muted">No BOLs match these filters</p>
                    <button type="button" className="mt-2 text-sm font-semibold text-fieldiq-text underline" onClick={clearFilters}>
                      Clear filters
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[900px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                        <th className="px-4 py-3">BOL number</th>
                        <th className="px-4 py-3">Scheduled</th>
                        <th className="px-4 py-3">Truck</th>
                        <th className="px-4 py-3">Driver</th>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Delivery location</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((bol: any) => {
                        const isCancelled = bol.status === 'cancelled';
                        return (
                          <tr
                            key={bol.id}
                            data-testid="bol-row"
                            tabIndex={0}
                            onClick={() => open(bol.id)}
                            onKeyDown={rowKeys(bol.id)}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${loadStatusSkin(bol.status).rail}`}
                          >
                            <td className={`whitespace-nowrap px-4 py-3 font-mono text-sm font-bold ${isCancelled ? 'text-fieldiq-muted line-through' : 'text-fieldiq-text'}`}>{bol.bol_number || '—'}</td>
                            {/* `scheduled_date` is a CALENDAR DAY. */}
                            <td className="whitespace-nowrap px-4 py-3 text-xs text-fieldiq-muted">{fmtDay(bol.scheduled_date)}</td>
                            <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-fieldiq-text">{bol.truck_unit_number || '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{bol.driver_name || '—'}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-fieldiq-text">{bol.customer_name || '—'}</td>
                            <td className="px-4 py-3 text-xs text-fieldiq-muted">{bol.delivery_location || '—'}</td>
                            <td className="px-4 py-3">
                              <LoadStatusBadge status={bol.status} />
                              {noInvoice(bol) && <NoInvoiceBadge className="ml-1.5" />}
                              <BilledOnLink coverage={coverageOf(bol)} className="ml-1.5" />
                              <MultipleInvoicesBadge coverage={coverageOf(bol)} className="ml-1.5" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((bol: any) => (
                    <div key={bol.id} className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
                    <button
                      type="button"
                      data-testid="bol-card"
                      onClick={() => open(bol.id)}
                      className="block w-full p-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`font-mono text-sm font-bold ${bol.status === 'cancelled' ? 'text-fieldiq-muted line-through' : 'text-fieldiq-text'}`}>{bol.bol_number || '—'}</p>
                          <p className="truncate text-xs text-fieldiq-muted">{bol.customer_name || '—'}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <LoadStatusBadge status={bol.status} />
                          {noInvoice(bol) && <NoInvoiceBadge />}
                          <MultipleInvoicesBadge coverage={coverageOf(bol)} />
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-fieldiq-text">
                        {fmtDay(bol.scheduled_date)} · {bol.driver_name || '—'} · {bol.truck_unit_number || '—'}
                      </p>
                    </button>
                    {coverageOf(bol).kind === 'billed' && (
                      <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
                        Invoice <BilledOnLink coverage={coverageOf(bol)} />
                      </p>
                    )}
                    </div>
                  ))}
                </div>
                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  {capped
                    ? `Showing ${filtered.length} of the ${BOL_LIMIT} most recent BOLs. Older ones are not listed here.`
                    : `Showing ${filtered.length} of ${bols?.length ?? 0} BOLs`}
                </p>
              </>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
